import {
  CONFLICT_STATE_LABEL,
  CONFLICT_STATE_TONE,
  DISPUTE_REASONS,
  DISPUTE_REASON_LABEL,
  type Conflict,
  type DecideConflict,
  type DisputeReason,
} from '@cyberscore/contracts';
import { Globe, Loader2, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { ApiError } from '../../lib/api';
import { hashQuery, href, navigate, useHash } from '../../lib/router';
import { useSession, can } from '../../lib/session';
import { useToast } from '../../ui/Toasts';
import { when } from '../clients/parts';
import { useDocuments } from '../documents/api';
import { useAssessmentConflicts, useConflictActions, useConflicts } from './api';

const message = (e: unknown, fallback: string) =>
  e instanceof ApiError ? (e.issues[0]?.message ?? e.message) : fallback;
const day = (iso: string) => new Date(iso).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' }); // prettier-ignore

function useRoles() {
  const { tenant } = useSession();

  return {
    decides: can(tenant, 'conflicts.decide'),
    disputes: can(tenant, 'conflicts.dispute'),
  };
}

/**
 * Conflicts (task 72): answers the internet check contradicts, across the book, for underwriters to
 * confirm or dismiss, and disputes to decide.
 */
export function ConflictsPage() {
  const hash = useHash();
  const tab = hashQuery(hash).get('tab') === 'settled' ? 'settled' : 'open';
  const list = useConflicts(tab);
  return (
    <>
      <div className="page-head">
        <h1>Internet check conflicts</h1>
        <p>Answers an outside-in check contradicts. Confirm one to use what the check saw (the answer loses its credit; a kill switch can apply), dismiss it, or decide the business’s dispute.</p>
      </div>
      <div className="seg list-tabs" role="group" aria-label="Show">
        <button type="button" aria-pressed={tab === 'open'} onClick={() => navigate(href.conflicts())}>To decide</button>
        <button type="button" aria-pressed={tab === 'settled'} onClick={() => navigate(href.conflicts('?tab=settled'))}>Settled</button>
      </div>
      {list.isPending ? <section className="card"><div className="empty">Loading…</div></section> : !list.data?.length ? (
        <section className="card"><div className="empty"><Globe size={28} /><strong>{tab === 'open' ? 'Nothing to decide' : 'Nothing settled yet'}</strong>When an outside-in check sees something an answer denies, it shows here.</div></section>
      ) : (
        <div className="stack">{list.data.map((c) => <ConflictItem key={c.id} c={c} showClient />)}</div>
      )}
    </>
  ); // prettier-ignore
}

/** On the assessment page: what the check saw that disagrees with the answers, for everyone who sees it. */
/** `canAnswer`: the business can still change its answers (otherwise it can only dispute). */
export function ConflictsCard({ assessmentId, canAnswer }: { assessmentId: string; canAnswer: boolean }) {
  const list = useAssessmentConflicts(assessmentId);
  const items = list.data ?? [];
  if (!items.length) return null;
  const open = items.filter((c) => c.state === 'open' || c.state === 'disputed');
  return (
    <section className="card pad conflicts-card" aria-labelledby="conflicts-title">
      <h2 id="conflicts-title" className="section-title"><ShieldAlert size={16} aria-hidden /> The internet check disagrees</h2>
      <p className="muted small">{open.length ? `${open.length} answer${open.length === 1 ? '' : 's'} the outside-in check contradicts.` : 'Every disagreement has been settled.'}</p>
      <div className="stack">{items.map((c) => <ConflictItem key={c.id} c={c} canAnswer={canAnswer} />)}</div>
    </section>
  ); // prettier-ignore
}

function ConflictItem({
  c,
  showClient = false,
  canAnswer = false,
}: {
  c: Conflict;
  showClient?: boolean;
  canAnswer?: boolean;
}) {
  const { decides, disputes } = useRoles();
  const [disputing, setDisputing] = useState(false);
  const [deciding, setDeciding] = useState<DecideConflict['outcome'] | null>(null);
  const open = c.state === 'open' || c.state === 'disputed';
  return (
    <article className="conflict" aria-label={`${c.customer_name}: ${c.control}`}>
      <div className="row-between">
        <div className="minw">
          {showClient && <a className="cell-title" href={href.assessment(c.assessment_id)}>{c.customer_name}</a>}
          <div className="small"><b>{c.control}</b> · {c.question_text}{c.kill_switch && <span className="pill bad small-pill">Kill switch</span>}</div>
        </div>
        <span className={`pill ${CONFLICT_STATE_TONE[c.state]}`}>{CONFLICT_STATE_LABEL[c.state]}</span>
      </div>
      <dl className="conflict-facts small">
        <dt>Answered</dt><dd>{c.answer.label}</dd>
        <dt>The check saw</dt><dd>{c.reading.reason} <span className="muted">({c.source_name}, {day(c.observed_at)})</span></dd>
        <dt>So the answer would be</dt><dd>{c.reading.suggests.label}</dd>
      </dl>
      {c.dispute && (
        <div className="conflict-note small">
          <b>Disputed:</b> {DISPUTE_REASON_LABEL[c.dispute.reason]}. “{c.dispute.note}” <span className="muted">{c.dispute.by ?? 'The business'}, {when(c.dispute.at)}</span>
          {!!c.dispute.documents.length && <div className="muted">Documents: {c.dispute.documents.map((d) => d.name).join(', ')}</div>}
        </div>
      )}
      {c.decision && (
        <div className="conflict-note small"><b>{CONFLICT_STATE_LABEL[c.state]}</b>{c.decision.note ? `: ${c.decision.note}` : ''} <span className="muted">{c.decision.by ?? 'Your insurer'}, {when(c.decision.at)}</span></div>
      )}
      {open && (
        <div className="row gap wrap">
          {decides && c.state === 'open' && <><button type="button" className="btn btn-sm" onClick={() => setDeciding('confirm')}>Confirm</button><button type="button" className="btn btn-sm btn-ghost" onClick={() => setDeciding('dismiss')}>Dismiss</button></>}
          {decides && c.state === 'disputed' && <><button type="button" className="btn btn-sm" onClick={() => setDeciding('uphold')}>Uphold the dispute</button><button type="button" className="btn btn-sm btn-ghost" onClick={() => setDeciding('reject')}>Reject it</button></>}
          {disputes && c.state === 'open' && !disputing && <button type="button" className="btn btn-sm" onClick={() => setDisputing(true)}>Dispute</button>}
          {disputes && canAnswer && c.state === 'open' && <a className="btn btn-sm btn-ghost" href={href.answer(c.assessment_id, c.question)}>Change my answer</a>}
          {c.state === 'disputed' && !decides && <span className="muted small">Your insurer will decide.</span>}
        </div>
      )}
      {disputing && <DisputeForm c={c} onDone={() => setDisputing(false)} />}
      {deciding && <DecideForm c={c} outcome={deciding} onDone={() => setDeciding(null)} />}
    </article>
  ); // prettier-ignore
}

const DECIDE_LABEL: Record<DecideConflict['outcome'], string> = { confirm: 'Confirm: use what the check saw', dismiss: 'Dismiss: the answer stands', uphold: 'Uphold: the answer stands', reject: 'Reject: use what the check saw' }; // prettier-ignore

function DecideForm({
  c,
  outcome,
  onDone,
}: {
  c: Conflict;
  outcome: DecideConflict['outcome'];
  onDone: () => void;
}) {
  const toast = useToast();
  const { decide } = useConflictActions();
  const [note, setNote] = useState('');
  const id = `cd-${c.id}`;
  const go = async () => {
    try {
      const r = await decide.mutateAsync({ id: c.id, outcome, note: note.trim() });
      toast(`${c.control}: ${CONFLICT_STATE_LABEL[r.state].toLowerCase()}.`);
      onDone();
    } catch (e) {
      toast(message(e, 'Could not record it'), 'bad');
    }
  };
  return (
    <div className="conflict-form">
      <label htmlFor={id} className="small">Note{c.state === 'disputed' ? ' (the business sees it)' : ' (optional)'}</label>
      <textarea id={id} className="input" rows={2} maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} />
      <div className="row gap"><button type="button" className="btn btn-primary btn-sm" disabled={decide.isPending} onClick={go}>{decide.isPending && <Loader2 size={14} className="spin" />}{DECIDE_LABEL[outcome]}</button><button type="button" className="btn btn-sm btn-ghost" onClick={onDone}>Cancel</button></div>
    </div>
  ); // prettier-ignore
}

function DisputeForm({ c, onDone }: { c: Conflict; onDone: () => void }) {
  const toast = useToast();
  const { dispute } = useConflictActions();
  const docs = useDocuments(c.assessment_id);
  const [reason, setReason] = useState<DisputeReason>('fixed');
  const [note, setNote] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [banner, setBanner] = useState<string | null>(null);
  const stored = (docs.data ?? []).filter((d) => d.status === 'stored');
  const go = async () => {
    setBanner(null);
    try {
      await dispute.mutateAsync({ id: c.id, reason, note: note.trim(), documents: picked });
      toast('Dispute sent to your insurer.');
      onDone();
    } catch (e) {
      setBanner(message(e, 'Could not send the dispute'));
    }
  };
  return (
    <div className="conflict-form">
      {banner && <div className="result bad" role="alert"><span>{banner}</span></div>}
      <fieldset className="field checks"><legend>Why is it wrong?</legend>
        {DISPUTE_REASONS.map((r) => <label key={r} className="toggle-row small"><input type="radio" name={`reason-${c.id}`} checked={reason === r} onChange={() => setReason(r)} /> <span>{DISPUTE_REASON_LABEL[r]}</span></label>)}
      </fieldset>
      <label htmlFor={`dn-${c.id}`} className="small">What happened</label>
      <textarea id={`dn-${c.id}`} className="input" rows={3} maxLength={2000} placeholder="For example: we closed remote desktop on 2 October; staff now use the VPN." value={note} onChange={(e) => setNote(e.target.value)} />
      {!!stored.length && (
        <fieldset className="field checks"><legend>Documents that show it (optional)</legend>
          {stored.map((d) => <label key={d.id} className="toggle-row small"><input type="checkbox" checked={picked.includes(d.id)} onChange={(e) => setPicked(e.target.checked ? [...picked, d.id] : picked.filter((x) => x !== d.id))} /> <span>{d.file_name}</span></label>)}
        </fieldset>
      )}
      <div className="row gap"><button type="button" className="btn btn-primary btn-sm" disabled={dispute.isPending} onClick={go}>{dispute.isPending && <Loader2 size={14} className="spin" />}Send the dispute</button><button type="button" className="btn btn-sm btn-ghost" onClick={onDone}>Cancel</button></div>
    </div>
  ); // prettier-ignore
}
